import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { SearchBar, Modal, TableFlat } from "@veorest/ui";
import { Users } from "lucide-react";
import { fetchGlobalCustomer, fetchGlobalCustomers } from "@veorest/api-client";
import { PageHeader } from "../components/shell/PageHeader";
import { AdminPager, AdminPanel } from "../components/shell/AdminPanel";
import { EmptyState } from "../components/data/EmptyState";
import { formatDate, formatNumber } from "../lib/format";

export default function CustomersGlobalPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["admin-customers-global", search, page],
    queryFn: () => fetchGlobalCustomers({ search, page, limit: 25 }),
  });

  const detail = useQuery({
    queryKey: ["admin-customer-global", selectedId],
    queryFn: () => fetchGlobalCustomer(selectedId!),
    enabled: Boolean(selectedId),
  });

  const totalPages = Math.max(1, Math.ceil((list.data?.total ?? 0) / 25));
  const items = (list.data?.items ?? []) as Array<Record<string, unknown> & { id: string }>;

  return (
    <div className="menu-page">
      <PageHeader
        eyebrow="Customers"
        title="Global customers"
        subtitle="OELOX Android app accounts — separate from client CRM."
      />

      <AdminPanel
        toolbar={
          <SearchBar
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            placeholder="Search username, name, phone…"
            className="admin-search"
          />
        }
      >
        {list.isLoading ? (
          <div className="space-y-2 p-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-14 rounded-xl skeleton" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Users className="h-5 w-5" />}
            title="No global customers"
            body="Platform app signups will appear here."
          />
        ) : (
          <div className="admin-table-wrap">
            <TableFlat
              columns={[
                {
                  key: "username",
                  header: "User",
                  render: (row) => (
                    <div className="admin-row-main">
                      <span className="admin-row-avatar admin-row-avatar--accent" aria-hidden>
                        {String(row.display_name || row.username || "?").slice(0, 1).toUpperCase()}
                      </span>
                      <div className="min-w-0">
                        <p className="admin-row-title">{String(row.display_name || row.username)}</p>
                        <p className="admin-row-sub">@{String(row.username)}</p>
                      </div>
                    </div>
                  ),
                },
                { key: "phone", header: "Phone", render: (row) => String(row.phone || "—") },
                {
                  key: "platform_order_count",
                  header: "App orders",
                  render: (row) => (
                    <span className="tabular font-bold">
                      {formatNumber(Number(row.platform_order_count))}
                    </span>
                  ),
                },
                {
                  key: "created_at",
                  header: "Joined",
                  render: (row) => (
                    <span className="text-[var(--text-muted)]">
                      {formatDate(String(row.created_at))}
                    </span>
                  ),
                },
              ]}
              data={items}
              keyField="id"
              onRowClick={(row) => setSelectedId(String(row.id))}
            />
          </div>
        )}
      </AdminPanel>

      <AdminPager
        page={page}
        totalPages={totalPages}
        total={list.data?.total ?? 0}
        label="customers"
        onPrev={() => setPage((p) => p - 1)}
        onNext={() => setPage((p) => p + 1)}
      />

      <Modal open={Boolean(selectedId)} onClose={() => setSelectedId(null)} panelClassName="max-w-2xl">
        <div className="p-6">
          <p className="menu-page__eyebrow mb-1">Global identity</p>
          <h2 className="text-xl font-black tracking-tight mb-4">Customer detail</h2>
          {detail.isLoading ? (
            <div className="h-24 skeleton rounded-xl" />
          ) : (
            <>
              <div className="admin-detail-grid mb-5">
                <div>
                  <p className="admin-detail-label">Username</p>
                  <p className="admin-detail-value">{String(detail.data?.customer.username)}</p>
                </div>
                <div>
                  <p className="admin-detail-label">Phone</p>
                  <p className="admin-detail-value">{String(detail.data?.customer.phone || "—")}</p>
                </div>
                <div>
                  <p className="admin-detail-label">Name</p>
                  <p className="admin-detail-value">
                    {String(detail.data?.customer.display_name || "—")}
                  </p>
                </div>
                <div>
                  <p className="admin-detail-label">Joined</p>
                  <p className="admin-detail-value">
                    {formatDate(String(detail.data?.customer.created_at))}
                  </p>
                </div>
              </div>
              <h3 className="font-bold mb-2">Order history</h3>
              <div className="admin-table-wrap max-h-64 overflow-auto">
                <table className="table-flat">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Restaurant</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.data?.orders as Array<Record<string, unknown>> ?? []).length === 0 ? (
                      <tr>
                        <td colSpan={3} className="text-center py-6 text-[var(--text-muted)]">
                          No orders
                        </td>
                      </tr>
                    ) : (
                      (detail.data?.orders as Array<Record<string, unknown>>).map((o) => (
                        <tr key={String(o.id)}>
                          <td>{formatDate(String(o.created_at))}</td>
                          <td>{String(o.restaurant_name || "—")}</td>
                          <td>{String(o.status)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}
